import {useCallback, useEffect, useRef, useState} from 'react';

import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getUserNewslettersHistory} from 'src/openapi/admin/apis/GetUserNewslettersHistoryApi';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {Error} from 'src/openapi/admin/models/Error';
import type {NewsletterHistoryFilters} from 'src/openapi/admin/models/NewsletterHistoryFilters';
import type {NewsletterHistoryRequest} from 'src/openapi/admin/models/NewsletterHistoryRequest';
import type {NewsletterSendEvent} from 'src/openapi/admin/models/NewsletterSendEvent';

export type NewsletterHistory = {
    request: NewsletterHistoryRequest,
    data: NewsletterSendEvent[],
};

type Props = {
    userId: number,
    availableNewsletterTypes: string[],
};

export const useNewsletterHistory = ({userId, availableNewsletterTypes}: Props) => {
    const {getMonthPeriod} = useDateTime();
    const apiDispatch = useAppDispatch();

    const [filters, setFilters] = useState<NewsletterHistoryFilters>({
        newsletterTypes: availableNewsletterTypes,
        period: getMonthPeriod(),
    });

    const initializedRef = useRef(false);
    useEffect(() => {
        if (availableNewsletterTypes.length && !initializedRef.current) {
            initializedRef.current = true;
            setFilters(prev => ({...prev, newsletterTypes: availableNewsletterTypes}));
        }
    }, [availableNewsletterTypes]);
    const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.DESC);

    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<NewsletterSendEvent[]>([]);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const loadHistory = useCallback(() => {
        setIsLoading(true);
        apiDispatch(getUserNewslettersHistory({
            userId,
            newsletterHistoryRequest: {filters, sortDirection}}),
        ).then(response => {
            setIsLoading(false);
            if (response.payload.success) {
                setData(response.payload.data);
            } else {
                setLoadingError(response.payload.data);
            }
        });
    }, [filters, sortDirection, apiDispatch, userId]);

    useEffect(() => {
        loadHistory();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            loadHistory();
        }
    }, {filters, sortDirection});

    const clearFilters = useCallback(() => {
        const defaultFilters = {
            newsletterTypes: availableNewsletterTypes,
            period: getMonthPeriod(),
        };

        setFilters(defaultFilters);
        setSortDirection(SortDirections.DESC);

        return defaultFilters;
    }, [availableNewsletterTypes, getMonthPeriod]);

    return ({
        isLoading,
        loadingError,

        data,
        sortDirection,
        setSortDirection,
        filters,

        applyFilters: setFilters,
        clearFilters,
    });
};
